import {damsFetch} from "../app/damsFetch";
import {DB_VERSION} from "./dbVersion";

/**
 * Creates and initializes an IndexedDB database.
 *
 * @function createIndexedDb
 * @returns {Promise<IDBDatabase>} A promise that resolves with the opened database instance.
 *
 * This function creates an IndexedDB database named 'dams' with the specified version.
 * It defines object stores and populates them with data if necessary.
 */
export const createIndexedDb = () => {
    const DB_NAME = 'dams';

    // List of object stores to be created in the database.
    const stores = ['object_definitions'];

    /**
     * Populates the 'object_definitions' store with data fetched from the server.
     *
     * @async
     * @function _populateObjectDefinitionsStore
     * @param {IDBDatabase} db - The database instance.
     * @param {string} storeName - The name of the object store to populate.
     * @returns {Promise<void>} A promise that resolves when the store is populated.
     */
    const _populateObjectDefinitionsStore = async (db, storeName) => {
        try {
            const res = await damsFetch('/object-definitions/', {method: 'GET'});
            const transaction = db.transaction(storeName, "readwrite");
            const store = transaction.objectStore(storeName);
            store.clear();
            store.put(res, 1);
            await transaction.complete;
        } catch (error) {
            console.error('failed to populate store:', error);
        }
    };

    /**
     * Populates a specific object store based on its name.
     *
     * @async
     * @function _populateStore
     * @param {IDBDatabase} db - The database instance.
     * @param {string} storeName - The name of the object store to populate.
     * @returns {Promise<void>} A promise that resolves when the store is populated.
     */
    const _populateStore = async (db, storeName) => {
        if (storeName === 'object_definitions') {
            await _populateObjectDefinitionsStore(db, storeName);
        }
    };

    /**
     * Opens the IndexedDB database and initializes object stores.
     *
     * @returns {Promise<IDBDatabase>} A promise that resolves with the opened database instance.
     */
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        // Event triggered when the database version is upgraded.
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            for (const storeName of stores) {
                if (!db.objectStoreNames.contains(storeName)) {
                    db.createObjectStore(storeName);
                }
            }
        };

        // Event triggered when the database is successfully opened.
        request.onsuccess = (event) => {
            const db = event.target.result;
            resolve(db);

            (async () => {
                for (const storeName of stores) {
                    await _populateStore(db, storeName);
                }
            })();
        };

        // Event triggered when there is an error opening the database.
        request.onerror = (event) => {
            reject(event.target.error);
        };
    });
};
